import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Globe, Volume2, ArrowLeft, Check, BookMarked } from 'lucide-react';
import { languages } from '../mockData';
import { toast } from '../hooks/use-toast';
import { contentAPI, progressAPI } from '../services/api';

const LearningInterface = () => {
  const navigate = useNavigate();
  const { category } = useParams();
  const [selectedLanguage, setSelectedLanguage] = useState('spanish');
  const [voiceGender, setVoiceGender] = useState('female');
  const [learnedItems, setLearnedItems] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoggedIn = !!localStorage.getItem('authToken');

  useEffect(() => {
    const lang = localStorage.getItem('selectedLanguage') || 'spanish';
    const voice = localStorage.getItem('voiceGender') || 'female';
    setSelectedLanguage(lang);
    setVoiceGender(voice);
    
    fetchContent(lang);
    if (isLoggedIn) fetchProgress(lang);
  }, [category]);

  const fetchContent = async (lang) => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching content from API:', lang, category);
      const data = await contentAPI.getContent(lang, category);
      console.log('Content received:', data);
      setContent(data.items || []);
      if (!data.items || data.items.length === 0) {
        setError('No content available for this category');
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setError(`Failed to load content: ${error.message}`);
      toast({
        title: 'Error',
        description: 'Failed to load content. Please check your internet connection.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async (lang) => {
    try {
      const progressData = await progressAPI.getProgress(lang);
      const learned = new Set(
        progressData.learned_items
          .filter(item => item.category === category)
          .map(item => `${category}-${item.item_id}`)
      );
      setLearnedItems(learned);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const speakText = (text, lang) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      
      // Language codes mapping
      const langCodes = {
        spanish: 'es-ES',
        french: 'fr-FR',
        german: 'de-DE',
        italian: 'it-IT',
        hindi: 'hi-IN',
        tamil: 'ta-IN',
        kannada: 'kn-IN',
        telugu: 'te-IN',
        malayalam: 'ml-IN'
      };

      utterance.lang = langCodes[lang] || 'en-US';
      utterance.rate = 0.85;
      
      // Wait for voices to load
      const setVoice = () => {
        const voices = window.speechSynthesis.getVoices();
        const targetLangCode = langCodes[lang];
        const currentVoiceGender = voiceGender; // Use state value
        
        console.log('Voice selection - Gender:', currentVoiceGender, 'Language:', lang);
        console.log('Available voices:', voices.map(v => ({ name: v.name, lang: v.lang })));
        
        if (voices.length > 0 && targetLangCode) {
          // Filter voices for the target language
          const langVoices = voices.filter(voice => 
            voice.lang.toLowerCase().startsWith(targetLangCode.substring(0, 2).toLowerCase())
          );
          
          console.log('Matching language voices:', langVoices.map(v => v.name));
          
          if (langVoices.length > 0) {
            let selectedVoice = null;
            
            // Common female voice name patterns
            const femalePatterns = [
              'female', 'woman', 'girl',
              'samantha', 'karen', 'victoria', 'monica', 'paulina', 'fiona',
              'amelie', 'marie', 'anna', 'helena', 'petra', 'sara', 'lucia',
              'google.*female', 'microsoft.*female',
              // Spanish female voices
              'conchita', 'penelope', 'lupe', 'mia',
              // French female voices  
              'celine', 'lea', 'virginie',
              // German female voices
              'marlene', 'vicki', 'hedda',
              // Italian female voices
              'alice', 'elsa', 'federica', 'bianca',
              // Indian language female voices
              'aditi', 'raveena', 'priya', 'tanishaa', 'veena', 'sobhana'
            ];
            
            // Common male voice name patterns
            const malePatterns = [
              'male', 'man', 'boy',
              'daniel', 'thomas', 'alex', 'david', 'james', 'mark', 'fred',
              'diego', 'jorge', 'juan', 'carlos', 'enrique', 'miguel',
              'jacques', 'mathieu', 'pierre',
              'hans', 'stefan', 'markus',
              'luca', 'giorgio', 'cosimo',
              'google.*male', 'microsoft.*male',
              // Indian male voices
              'ravi', 'vijay', 'karthik', 'ajit'
            ];
            
            const patterns = currentVoiceGender === 'female' ? femalePatterns : malePatterns;
            
            // Try to find a voice matching gender preference
            for (const pattern of patterns) {
              const regex = new RegExp(pattern, 'i');
              selectedVoice = langVoices.find(v => regex.test(v.name));
              if (selectedVoice) {
                console.log('Selected voice by pattern:', selectedVoice.name);
                break;
              }
            }
            
            // If still no match, try to avoid opposite gender
            if (!selectedVoice) {
              const oppositePatterns = currentVoiceGender === 'female' ? malePatterns : femalePatterns;
              selectedVoice = langVoices.find(v => {
                const name = v.name.toLowerCase();
                return !oppositePatterns.some(p => new RegExp(p, 'i').test(name));
              });
              console.log('Selected voice by exclusion:', selectedVoice?.name);
            }
            
            // Fallback to first available voice for the language
            if (!selectedVoice) {
              selectedVoice = langVoices[0];
              console.log('Fallback to first voice:', selectedVoice.name);
            }
            
            utterance.voice = selectedVoice;
          }
        }
        
        window.speechSynthesis.speak(utterance);
      };
      
      // Voices might not be loaded yet, so we need to wait
      if (window.speechSynthesis.getVoices().length > 0) {
        setVoice();
      } else {
        window.speechSynthesis.onvoiceschanged = setVoice;
      }
    } else {
      toast({
        title: 'Text-to-speech not supported',
        description: 'Your browser does not support text-to-speech',
        variant: 'destructive',
      });
    }
  };

  const markAsLearned = async (id, index) => {
    if (!isLoggedIn) {
      toast({ title: 'Sign up to save progress', description: 'Create a free account to track your learning', variant: 'destructive' });
      return;
    }
    const newLearned = new Set(learnedItems);
    if (newLearned.has(id)) {
      newLearned.delete(id);
      toast({ title: 'Unmarked', description: 'Item removed from learned list' });
    } else {
      newLearned.add(id);
      
      // Save to backend
      try {
        await progressAPI.markLearned(selectedLanguage, category, String(index));
        toast({ title: 'Great progress!', description: 'Item marked as learned' });
      } catch (error) {
        console.error('Error marking learned:', error);
        newLearned.delete(id);
        toast({
          title: 'Error',
          description: 'Failed to save progress',
          variant: 'destructive',
        });
        return;
      }
    }
    setLearnedItems(newLearned);
  };

  const getContent = () => {
    return content;
  };

  const content_data = getContent();
  const selectedLang = languages.find(l => l.id === selectedLanguage);
  const categoryTitle = category === 'nouns' ? 'Common Nouns' : 
                        category === 'verbs' ? 'Common Verbs' : 'Common Questions';

  const filteredContent = activeTab === 'all' ? content_data : 
                          activeTab === 'learned' ? content_data.filter((_, idx) => learnedItems.has(`${category}-${idx}`)) :
                          content_data.filter((_, idx) => !learnedItems.has(`${category}-${idx}`));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-sky-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error && content.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-sky-50 px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Content</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <div className="space-y-2">
            <Button onClick={() => fetchContent(selectedLanguage)} className="bg-amber-600 hover:bg-amber-700">
              Try Again
            </Button>
            <br />
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Globe className="h-8 w-8 text-amber-600" />
                <span className="text-xl font-bold text-slate-900">NomadLinguist</span>
              </div>
            </div>
            <Badge variant="secondary" className="text-sm">
              {selectedLang?.name} - {voiceGender === 'female' ? 'Female' : 'Male'} Voice
            </Badge>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Guest Banner */}
        {!isLoggedIn && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between" data-testid="guest-banner">
            <p className="text-amber-800 text-sm font-medium">Sign up to save your progress and unlock all features</p>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 ml-4 shrink-0" onClick={() => navigate('/register')}>
              Sign Up Free
            </Button>
          </div>
        )}
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{categoryTitle}</h1>
          <p className="text-xl text-slate-600">
            Learning {selectedLang?.name} - Click the speaker icon to hear pronunciations
          </p>
        </div>

        {/* Filter Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList>
            <TabsTrigger value="all">All ({content_data.length})</TabsTrigger>
            <TabsTrigger value="learned">Learned ({learnedItems.size})</TabsTrigger>
            <TabsTrigger value="unlearned">To Learn ({content_data.length - learnedItems.size})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Content Grid */}
        <div className="space-y-4">
          {filteredContent.map((item, index) => {
            const originalIndex = content_data.indexOf(item);
            const itemId = `${category}-${originalIndex}`;
            const isLearned = learnedItems.has(itemId);
            
            return (
              <Card 
                key={index} 
                className={`border-2 hover:shadow-lg transition-all ${
                  isLearned ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200'
                }`}
              >
                <CardContent className="p-6">
                  {category === 'questions' ? (
                    <div>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <p className="text-lg font-semibold text-slate-900 mb-2">{item.english}</p>
                          <div className="bg-white p-4 rounded-lg border mb-2">
                            <p className="text-2xl font-bold text-amber-700 mb-1">{item.native}</p>
                            <p className="text-slate-600 italic">{item.transliteration}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            size="icon"
                            variant="outline"
                            className="bg-sky-100 hover:bg-sky-200 border-sky-300"
                            onClick={() => speakText(item.native, selectedLanguage)}
                          >
                            <Volume2 className="h-4 w-4 text-sky-700" />
                          </Button>
                          <Button
                            size="icon"
                            variant={isLearned ? "default" : "outline"}
                            className={isLearned ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                            onClick={() => markAsLearned(itemId, originalIndex)}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {item.responses && item.responses.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-semibold text-slate-700 mb-2">Typical Responses:</p>
                          <div className="flex flex-wrap gap-2">
                            {item.responses.map((response, idx) => (
                              <div 
                                key={idx}
                                className="bg-slate-100 px-3 py-2 rounded-lg flex items-center gap-2 group cursor-pointer hover:bg-slate-200 transition-colors"
                                onClick={() => speakText(response, selectedLanguage)}
                              >
                                <span className="text-slate-800">{response}</span>
                                <Volume2 className="h-3 w-3 text-slate-500 group-hover:text-slate-700" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-baseline gap-4 mb-2">
                          <p className="text-lg font-semibold text-slate-900">{item.english}</p>
                          <p className="text-2xl font-bold text-amber-700">{item.native}</p>
                        </div>
                        <p className="text-slate-600 italic">{item.transliteration}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="icon"
                          variant="outline"
                          className="bg-sky-100 hover:bg-sky-200 border-sky-300"
                          onClick={() => speakText(item.native, selectedLanguage)}
                        >
                          <Volume2 className="h-4 w-4 text-sky-700" />
                        </Button>
                        <Button
                          size="icon"
                          variant={isLearned ? "default" : "outline"}
                          className={isLearned ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                          onClick={() => markAsLearned(itemId, originalIndex)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredContent.length === 0 && (
          <div className="text-center py-12">
            <BookMarked className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-xl text-slate-500">No items in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningInterface;